import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Globe, Lightbulb, Target, CalendarCheck, Link2, MessageCircle, LogOut, Shield,
  Loader2, Sparkles, Share2, CreditCard, ShoppingBag, Settings as SettingsIcon, Users,
  Package, BarChart3, Wallet, Repeat, UtensilsCrossed, Wand2, UserCog, HandHeart,
  Inbox, ChevronDown, Lock,
} from "lucide-react";

import {
  PILL, PlanPill, BusinessSwitcher, LockedFeature,
} from "@/components/dashboard/shared";
import WebsiteTab from "@/components/dashboard/WebsiteTab";
import SettingsTab from "@/components/dashboard/SettingsTab";
import ShareTab from "@/components/dashboard/ShareTab";
import IdeaTab from "@/components/dashboard/IdeaTab";
import AudienceTab from "@/components/dashboard/AudienceTab";
import PlanTab from "@/components/dashboard/PlanTab";
import DomainTab from "@/components/dashboard/DomainTab";
import BillingTab from "@/components/dashboard/BillingTab";
import SupportTab from "@/components/dashboard/SupportTab";
import TeamTab from "@/components/dashboard/TeamTab";
import ProductsTab from "@/components/dashboard/ProductsTab";
import PaymentsTab from "@/components/dashboard/PaymentsTab";
import SubscriptionsTab from "@/components/dashboard/SubscriptionsTab";
import AnalyticsTab from "@/components/dashboard/AnalyticsTab";
import IncomeTab from "@/components/dashboard/IncomeTab";
import AccountTab from "@/components/dashboard/AccountTab";
import TrialExpiredGate from "@/components/dashboard/TrialExpiredGate";
import ReservationsTab from "@/components/dashboard/ReservationsTab";
import InlineVisualEditor from "@/components/dashboard/inline-editor/InlineVisualEditor";
import ContactsTab from "@/components/dashboard/ContactsTab";

const TAB_GROUPS = [
  {
    label: "Website",
    tabs: [
      { id: "website", label: "My Website", icon: Globe },
      { id: "visual", label: "Visual Editor", icon: Wand2 },
      { id: "settings", label: "Website Settings", icon: SettingsIcon },
      { id: "share", label: "Share Card", icon: Share2 },
    ],
  },
  {
    label: "Sell",
    tabs: [
      { id: "products", label: "Products & Services", icon: Package },
      { id: "reservations", label: "Reservations", icon: UtensilsCrossed, restaurantOnly: true },
      { id: "income", label: "My Income", icon: Wallet },
      { id: "payments", label: "Payments", icon: CreditCard },
      { id: "subscriptions", label: "Subscriptions", icon: Repeat },
      { id: "analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Grow",
    tabs: [
      { id: "contacts", label: "Contacts", icon: Inbox },
      { id: "idea", label: "My Business Idea", icon: Lightbulb },
      { id: "audience", label: "My Audience", icon: Target },
      { id: "plan", label: "30-Day Plan", icon: CalendarCheck },
      { id: "designer", label: "Hire a Designer", icon: HandHeart, comingSoon: true },
    ],
  },
  {
    label: "Account",
    tabs: [
      { id: "domain", label: "Domains", icon: Link2 },
      { id: "team", label: "Team Members", icon: Users },
      { id: "billing", label: "Billing", icon: CreditCard },
      { id: "account", label: "My Account", icon: UserCog },
      { id: "support", label: "Support", icon: MessageCircle },
    ],
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [biz, setBiz] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("website");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [unreadLeads, setUnreadLeads] = useState(0);
  const toggleGroup = (label) =>
    setCollapsedGroups((m) => ({ ...m, [label]: !m[label] }));
  const [plan, setPlan] = useState(null);
  const [activeBizId, setActiveBizId] = useState(() => localStorage.getItem("iti.activeBizId") || null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (activeBizId) localStorage.setItem("iti.activeBizId", activeBizId);
    api.defaults.params = activeBizId ? { business_id: activeBizId } : {};
  }, [activeBizId]);

  const loadBiz = async () => {
    try {
      const { data } = await api.get("/business");
      if (!data || !data.business_id) { navigate("/onboarding"); return; }
      setBiz(data);
      if (!activeBizId) setActiveBizId(data.business_id);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadBusinesses = async () => {
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data.businesses || []);
    } catch { /* non-fatal */ }
  };

  const loadPlan = async () => {
    try {
      const { data } = await api.get("/billing/plan");
      setPlan(data);
    } catch { /* non-fatal */ }
  };

  const loadUnreadLeads = async () => {
    try {
      const { data } = await api.get("/leads/unread-count");
      setUnreadLeads(data.unread || 0);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { if (user) { loadBiz(); loadPlan(); loadBusinesses(); loadUnreadLeads(); } }, [user]);
  useEffect(() => { if (user && activeBizId) { loadBiz(); loadUnreadLeads(); } }, [activeBizId]);

  const canCreateMoreBusinesses = !!plan?.can_create_business && plan?.plan_id !== "free";

  const switchBusiness = (business_id) => {
    if (business_id === activeBizId) return;
    setActiveBizId(business_id);
    setLoading(true);
    toast.success("Switched business");
  };

  const createNewBusiness = () => {
    if (!canCreateMoreBusinesses) {
      if (plan?.plan_id === "free") {
        toast.error("Upgrade to Launch, Growth or Scale to add another business");
      } else if (plan?.plan_id === "launch") {
        toast.error("You've reached the 10-business limit on Launch. Upgrade to Growth or Scale for unlimited.");
      } else {
        toast.error("Upgrade your plan to add another business");
      }
      setActiveTab("billing");
      return;
    }
    localStorage.setItem("iti.multiBizMode", "new");
    navigate("/onboarding?new=1");
  };

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    const purpose = params.get("purpose");

    // Designer marketplace checkout return
    if (purpose === "designer_order") {
      setActiveTab("designer");
      const pollDesigner = async (attempt = 0) => {
        try {
          const { data } = await api.get(`/designer/orders/status/${sessionId}`);
          if (data.status === "active" || data.payment_status === "paid") {
            toast.success("Payment received — your designer will begin shortly.");
            window.history.replaceState({}, "", "/dashboard");
            // Force a reload of the tab data so the new active order shows up
            window.dispatchEvent(new Event("designer:refresh"));
            return;
          }
          if (attempt < 8) setTimeout(() => pollDesigner(attempt + 1), 2000);
          else window.history.replaceState({}, "", "/dashboard");
        } catch { /* swallow — retried by poll loop */ }
      };
      pollDesigner();
      return;
    }

    // Main-plan subscription checkout return (Phase B)
    if (purpose === "main_plan") {
      setActiveTab("billing");
      const pollMain = async (attempt = 0) => {
        try {
          const { data } = await api.get(`/billing/subscription/checkout/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success(`Your ${data.plan_id ? data.plan_id + " " : ""}plan is now active.`);
            loadPlan();
            window.history.replaceState({}, "", "/dashboard");
            return;
          }
          if (data.status === "expired") {
            toast.error("Checkout session expired");
            window.history.replaceState({}, "", "/dashboard");
            return;
          }
          if (attempt < 8) setTimeout(() => pollMain(attempt + 1), 2000);
        } catch { /* retried by poll loop */ }
      };
      pollMain();
      return;
    }

    // Default: plan / solo domain / ai_credits checkout
    setActiveTab("billing");
    const poll = async (attempt = 0) => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Welcome to Pro! Your plan is now active.");
          loadPlan();
          window.history.replaceState({}, "", "/dashboard");
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired");
          window.history.replaceState({}, "", "/dashboard");
          return;
        }
        if (attempt < 8) setTimeout(() => poll(attempt + 1), 2000);
      } catch { /* swallow — retried by poll loop */ }
    };
    poll();
  }, []);

  // Listen for deep-link jumps fired from the SRC visual editor's "Sections
  // & content" panel — lets the customer hop from the editor to the tab
  // that owns bulk content (products, menu items, reservations, etc.).
  useEffect(() => {
    const onGoto = (e) => {
      const tab = e?.detail;
      if (typeof tab === "string" && tab) {
        setActiveTab(tab);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener("visualeditor:goto", onGoto);
    return () => window.removeEventListener("visualeditor:goto", onGoto);
  }, []);

  // Handle Stripe Connect onboarding return (?ecom=connected | ?ecom=refresh).
  // The connected URL fires when Stripe sends the merchant back after they
  // either finished onboarding OR clicked "Save and exit" — we re-fetch the
  // status to know which it was, then surface the right toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ecom = params.get("ecom");
    if (!ecom) return;
    setActiveTab("payments");
    const refreshAndToast = async () => {
      try {
        const { data } = await api.get("/ecommerce/stripe-connect/status");
        if (data.charges_enabled) {
          toast.success("Payments are live — your customers can now check out");
        } else if (data.connected && !data.charges_enabled) {
          toast.info("Stripe still needs a few more details — finish up in the Payments tab");
        } else if (ecom === "refresh") {
          toast.info("Continuing your Stripe setup…");
        }
      } catch {
        toast.error("Couldn't refresh Stripe status — open the Payments tab to retry");
      } finally {
        window.history.replaceState({}, "", "/dashboard");
      }
    };
    refreshAndToast();
  }, []);

  // Surface ecommerce demo-mode state once we know the biz. The banner only
  // shows for ecommerce sites that haven't finished Stripe Connect onboarding —
  // gives a one-click jump to the Payments tab so the merchant can switch from
  // the localStorage demo checkout to real Stripe.
  const [paymentsStatus, setPaymentsStatus] = useState(null);
  useEffect(() => {
    if (!biz?.business_id || biz?.industry_template !== "ecommerce") {
      setPaymentsStatus(null);
      return;
    }
    api.get("/ecommerce/stripe-connect/status").then((r) => setPaymentsStatus(r.data)).catch(() => {});
  }, [biz?.business_id, biz?.industry_template]);
  const showDemoBanner = !!(
    biz?.industry_template === "ecommerce" &&
    paymentsStatus &&
    paymentsStatus.connect_supported &&
    !paymentsStatus.charges_enabled &&
    activeTab !== "payments"
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (!biz) return null;

  const onUpgrade = () => setActiveTab("billing");

  // Free-trial paywall gate. When the 30-day trial has ended and the user
  // hasn't upgraded, we render a single upgrade CTA in place of the entire
  // dashboard. The user's website data stays intact — this is a UI-level
  // access lock only. `activeTab === "billing"` bypasses the gate so the
  // user can actually complete their upgrade from within it.
  if (plan?.free_expired && plan?.plan_id === "free" && activeTab !== "billing") {
    return (
      <TrialExpiredGate
        onUpgrade={onUpgrade}
        onLogout={logout}
        userEmail={user?.email}
      />
    );
  }

  // Full-screen visual editor takes over the entire viewport (no dashboard
  // chrome). Users exit back to the dashboard via the editor's own Exit
  // button, which flips the active tab back to "website".
  //
  // Iter 73 — InlineVisualEditor is the ONLY website editor. For businesses
  // whose industry_template isn't one of the SRC families (service,
  // restaurant, ecommerce), we show a small empty state instead of the
  // long-deleted legacy section-block canvas. Contact support if this
  // happens to a real account — most likely the biz was created before
  // industry_template was mandatory.
  const SRC_TEMPLATES = new Set(["service", "restaurant", "ecommerce"]);
  if (activeTab === "visual") {
    if (biz && SRC_TEMPLATES.has(biz.industry_template)) {
      return (
        <InlineVisualEditor
          biz={biz}
          onExit={() => setActiveTab("website")}
          onBusinessChange={loadBiz}
        />
      );
    }
    return (
      <div className="min-h-screen bg-[#FFFDF0] flex items-center justify-center px-6" data-testid="visual-editor-unsupported">
        <div className="max-w-md w-full bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0_0_#111] p-8 text-center">
          <div className="text-2xl font-extrabold tracking-tight mb-2">Visual editor not available</div>
          <p className="text-sm text-slate-600 mb-6">
            This business doesn&apos;t have a supported template yet. The Visual Editor works with
            service, restaurant, and ecommerce templates. Please contact support.
          </p>
          <button
            onClick={() => setActiveTab("website")}
            data-testid="visual-editor-unsupported-back"
            className="iti-btn-outline px-4 py-2 text-sm font-semibold"
          >
            Back to My Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF0]">
      <header className="border-b-2 border-black bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/iti-logo.png" alt="Interest To Income" className="w-10 h-10 rounded-lg border-2 border-black bg-white object-contain nb-shadow-sm p-0.5" />
            <div>
              <div className="font-display text-lg font-extrabold leading-none tracking-tight">Interest To Income</div>
              <div className="text-xs text-gray-600 mt-0.5">{biz.summary?.name || "Your business"}</div>
            </div>
            <BusinessSwitcher
              businesses={businesses}
              activeId={biz.business_id}
              onSwitch={switchBusiness}
              onCreate={createNewBusiness}
              canCreate={canCreateMoreBusinesses}
            />
          </div>
          <div className="flex items-center gap-3">
            {plan && <PlanPill plan={plan} onClick={onUpgrade} />}
            {user?.is_admin && (
              <button onClick={() => navigate("/admin")} className={`${PILL} bg-[#FFE6EC]`} data-testid="admin-btn">
                <Shield className="w-4 h-4" /> Admin
              </button>
            )}
            <div className="hidden md:flex items-center gap-2 text-sm">
              {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-black" />}
              <span className="font-semibold">{user?.name}</span>
            </div>
            <button onClick={async () => { await logout(); navigate("/"); }} className={`${PILL} bg-white`} data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="space-y-5">
          {TAB_GROUPS.map((group) => {
            const collapsed = !!collapsedGroups[group.label];
            return (
            <div key={group.label}>
              <button
                onClick={() => toggleGroup(group.label)}
                aria-expanded={!collapsed}
                data-testid={`group-toggle-${group.label.toLowerCase()}`}
                className="w-full flex items-center justify-between px-2 mb-2 text-[10px] uppercase tracking-widest font-extrabold text-gray-500 hover:text-black transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  {group.label}
                  {group.label === "Grow" && collapsed && unreadLeads > 0 && (
                    <span className="w-2 h-2 rounded-full bg-[#3B82F6]" data-testid="group-grow-unread-dot" aria-label={`${unreadLeads} unread contacts`} />
                  )}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
              </button>
              {!collapsed && (
              <div className="space-y-2">
                {group.tabs
                  .filter((t) => !t.restaurantOnly || biz?.industry_template === "restaurant")
                  .map((t) => {
                  const showAlert =
                    t.id === "payments" &&
                    biz?.industry_template === "ecommerce" &&
                    paymentsStatus?.connect_supported &&
                    !paymentsStatus?.charges_enabled &&
                    activeTab !== "payments";
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      data-testid={`tab-${t.id}`}
                      className={`w-full text-left px-4 py-3 rounded-xl font-semibold border-2 border-black flex items-center gap-3 nb-press relative ${
                        activeTab === t.id ? "bg-[#111] text-white" : "bg-white text-black"
                      }`}
                    >
                      <t.icon className="w-5 h-5" />
                      <span className="flex-1">{t.label}</span>
                      {t.comingSoon && (
                        <Lock
                          className={`w-4 h-4 ${activeTab === t.id ? "text-white" : "text-gray-500"}`}
                          data-testid={`tab-${t.id}-lock-icon`}
                          aria-label="Coming soon"
                        />
                      )}
                      {t.id === "contacts" && unreadLeads > 0 && (
                        <span
                          className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-extrabold flex items-center justify-center ${
                            activeTab === t.id ? "bg-white text-black" : "bg-[#3B82F6] text-white"
                          }`}
                          data-testid="tab-contacts-unread-badge"
                        >
                          {unreadLeads > 99 ? "99+" : unreadLeads}
                        </span>
                      )}
                      {showAlert && (
                        <span
                          className="w-2.5 h-2.5 rounded-full bg-[#FB923C]"
                          data-testid={`tab-${t.id}-alert-dot`}
                          aria-label="Action required"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </aside>

        <main className="min-w-0" data-testid="dashboard-main">
          {showDemoBanner && (
            <div className="nb-card p-4 mb-5 bg-[#FFF7E6] flex items-start gap-4" data-testid="demo-mode-banner">
              <div className="w-10 h-10 rounded-full bg-[#FFE0A6] flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold">Your store is in demo mode</div>
                <div className="text-sm text-gray-700 mt-0.5">
                  Customers can browse + add to cart, but checkout creates a mock order. Connect Stripe (~3 minutes) to take real payments.
                </div>
              </div>
              <button
                onClick={() => setActiveTab("payments")}
                className={`${PILL} bg-[#635BFF] text-white whitespace-nowrap`}
                data-testid="demo-mode-banner-cta"
              >
                Set up payments
              </button>
            </div>
          )}
          {activeTab === "website" && <WebsiteTab biz={biz} setBiz={setBiz} reload={loadBiz} plan={plan} onUpgrade={onUpgrade} />}
          {activeTab === "settings" && <SettingsTab biz={biz} reload={loadBiz} />}
          {activeTab === "share" && <ShareTab biz={biz} reload={loadBiz} />}
          {activeTab === "idea" && <IdeaTab biz={biz} setBiz={setBiz} />}
          {activeTab === "audience" && (plan?.has_audience
            ? <AudienceTab biz={biz} setBiz={setBiz} />
            : <LockedFeature title="Target audience data" plan={plan} onUpgrade={onUpgrade} />)}
          {activeTab === "plan" && (plan?.has_plan_30day
            ? <PlanTab biz={biz} setBiz={setBiz} />
            : <LockedFeature title="30-day action plan" plan={plan} onUpgrade={onUpgrade} />)}
          {activeTab === "domain" && <DomainTab biz={biz} setBiz={setBiz} reload={loadBiz} plan={plan} onUpgrade={onUpgrade} />}
          {activeTab === "billing" && <BillingTab plan={plan} reloadPlan={loadPlan} />}
          {activeTab === "account" && <AccountTab />}
          {activeTab === "support" && <SupportTab />}
          {activeTab === "team" && <TeamTab plan={plan} biz={biz} onUpgrade={onUpgrade} />}
          {activeTab === "products" && <ProductsTab biz={biz} />}
          {activeTab === "income" && <IncomeTab biz={biz} />}
          {activeTab === "reservations" && <ReservationsTab biz={biz} />}
          {activeTab === "payments" && <PaymentsTab biz={biz} reload={loadBiz} />}
          {activeTab === "subscriptions" && <SubscriptionsTab biz={biz} />}
          {activeTab === "analytics" && <AnalyticsTab biz={biz} />}
          {activeTab === "designer" && (
            <div
              className="nb-card p-10 text-center max-w-2xl mx-auto"
              data-testid="designer-coming-soon"
            >
              <div className="w-20 h-20 rounded-2xl bg-[#FFE6EC] border-2 border-black mx-auto flex items-center justify-center mb-5">
                <Lock className="w-9 h-9" />
              </div>
              <div className="font-display text-3xl font-extrabold" data-testid="designer-coming-soon-title">
                Hire a Designer
              </div>
              <div
                className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-widest bg-[#111] text-white border-2 border-black"
                data-testid="designer-coming-soon-badge"
              >
                Coming Soon
              </div>
              <p className="text-gray-700 mt-4 max-w-md mx-auto" data-testid="designer-coming-soon-detail">
                We&apos;re polishing this feature. The designer marketplace will be back soon — check in later.
              </p>
            </div>
          )}
          {activeTab === "contacts" && <ContactsTab onUnreadChange={setUnreadLeads} />}
        </main>
      </div>
    </div>
  );
}

